import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { SquareImageUpload } from '@/components/molecules/square-image-upload.molecule'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { AdminOnlyNotice } from './admin-only-notice.atom'
import type { OrgProfileSectionProps } from './org-settings.interface'
import { type OrgProfileFormSchemaType, orgProfileFormSchema } from './org-settings.schema'

const OrgProfileSectionBase: React.FC<OrgProfileSectionProps> = ({
  name,
  imageUrl,
  onNameChange,
  onLogoChange,
  onLogoRemove,
  isLoading = false,
  isDisabled = false,
  className,
}): ReactNode => {
  const form = useForm<OrgProfileFormSchemaType>({
    resolver: zodResolver(orgProfileFormSchema),
    defaultValues: { name },
    mode: 'onChange',
  })

  useEffect(() => {
    form.reset({ name })
  }, [name, form])

  const handleSubmit = (data: OrgProfileFormSchemaType): void => {
    onNameChange(data.name)
  }

  return (
    <section className={cn('org-profile-section', className)}>
      <OrgProfileHeader />
      {isDisabled && <AdminOnlyNotice />}
      <OrgProfileFormContent
        form={form}
        imageUrl={imageUrl}
        name={name}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onLogoChange={onLogoChange}
        onLogoRemove={onLogoRemove}
        onSubmit={handleSubmit}
      />
    </section>
  )
}

const OrgProfileHeader: React.FC = () => (
  <div className="org-profile-header mb-4">
    <h3 className="text-lg font-medium">Organization Profile</h3>
    <p className="text-sm text-muted-foreground">
      Manage your organization&apos;s logo and display name.
    </p>
  </div>
)

interface OrgProfileFormContentProps {
  form: ReturnType<typeof useForm<OrgProfileFormSchemaType>>
  imageUrl: string | null
  name: string
  isLoading: boolean
  isDisabled: boolean
  onLogoChange: (file: File | null) => void
  onLogoRemove: () => void
  onSubmit: (data: OrgProfileFormSchemaType) => void
}

const OrgProfileFormContent: React.FC<OrgProfileFormContentProps> = ({
  form,
  imageUrl,
  name,
  isLoading,
  isDisabled,
  onLogoChange,
  onLogoRemove,
  onSubmit,
}) => {
  const { isDirty, isValid } = form.formState
  const effectiveDisabled = isLoading || isDisabled

  return (
    <div className="org-profile-form-wrapper flex flex-col gap-6 sm:flex-row sm:items-start">
      <div className="org-profile-logo-wrapper shrink-0">
        <SquareImageUpload
          imageUrl={imageUrl}
          fallbackText={name}
          onImageChange={onLogoChange}
          onImageRemove={onLogoRemove}
          isDisabled={effectiveDisabled}
          size="lg"
          uploadTooltip="Upload organization logo"
          changeTooltip="Change organization logo"
          removeTooltip="Remove organization logo"
        />
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="org-profile-name-form flex-1 space-y-4"
        >
          <OrgNameFormField control={form.control} isDisabled={effectiveDisabled} />
          {!isDisabled && isDirty && (
            <OrgProfileSaveButton isValid={isValid} isLoading={isLoading} />
          )}
        </form>
      </Form>
    </div>
  )
}

interface OrgNameFormFieldProps {
  control: ReturnType<typeof useForm<OrgProfileFormSchemaType>>['control']
  isDisabled: boolean
}

const OrgNameFormField: React.FC<OrgNameFormFieldProps> = ({ control, isDisabled }) => (
  <FormField
    control={control}
    name="name"
    render={({ field }) => (
      <FormItem>
        <FormLabel>Organization Name</FormLabel>
        <FormControl>
          <Input
            {...field}
            placeholder="Enter organization name"
            disabled={isDisabled}
            className="org-profile-name-input max-w-md"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
)

interface OrgProfileSaveButtonProps {
  isValid: boolean
  isLoading: boolean
}

const OrgProfileSaveButton: React.FC<OrgProfileSaveButtonProps> = ({ isValid, isLoading }) => (
  <Button type="submit" disabled={!isValid || isLoading} className="org-profile-save-button">
    {isLoading ? (
      <>
        <Loader2Icon className="mr-2 size-4 animate-spin" />
        Saving...
      </>
    ) : (
      'Save Name'
    )}
  </Button>
)

export const OrgProfileSection = memo(OrgProfileSectionBase)

OrgProfileSection.displayName = 'OrgProfileSection'
